<template>
  <button
    type="button"
    class="constraint-card"
    :data-testid="`constraint-list-card-${constraint.id}`"
    :data-constraint-type="constraint.type"
    :data-constraint-status="constraint.status"
    @click="$emit('select', constraint)"
  >
    <div class="constraint-card__header">
      <div class="constraint-card__title-row">
        <span
          class="constraint-card__status-dot"
          :class="`constraint-card__status-dot--${statusKey}`"
        />
        <h3 class="constraint-card__title">{{ title }}</h3>
      </div>
      <div class="constraint-card__badges">
        <span
          v-if="typeBadgeLabel"
          class="constraint-card__type-badge"
          :class="`constraint-card__type-badge--${typeKey}`"
        >
          {{ typeBadgeLabel }}
        </span>
        <span
          class="constraint-card__status-badge"
          :class="`constraint-card__status-badge--${statusKey}`"
        >
          {{ statusLabel }}
        </span>
      </div>
    </div>

    <div class="constraint-card__meta">
      <span>{{ reportedLine }}</span>
      <span v-if="constraint.resolvedAt">· {{ resolvedLine }}</span>
      <span v-if="constraint.milestoneTitle">· {{ constraint.milestoneTitle }}</span>
    </div>

    <div class="constraint-card__footer">
      <div v-if="constraint.ownerDisplayName" class="constraint-card__owner">
        <span class="constraint-card__avatar">{{ ownerInitials }}</span>
        <span class="constraint-card__owner-name">{{ constraint.ownerDisplayName }}</span>
      </div>
      <span
        v-if="constraint.commitmentId"
        class="constraint-card__linked"
      >
        ·
        <i class="pi pi-flag text-xs" aria-hidden="true" />
        {{ $t('UserProjects.constraints.fromCause') }}
      </span>
    </div>
  </button>
</template>

<script>
import {
  displayConstraintTitle,
  isActiveConstraintStatus,
} from '@/utils/constraintFormMapping';
import {
  formatConstraintDate,
  getConstraintPriority,
  userInitials,
} from '@/utils/constraintDisplay';

export default {
  name: 'ConstraintCard',
  props: {
    constraint: {
      type: Object,
      required: true,
    },
  },
  emits: ['select'],
  computed: {
    title() {
      return displayConstraintTitle(this.constraint);
    },
    statusKey() {
      if (this.constraint.status === 'resolved') return 'resolved';
      if (this.constraint.status === 'in_progress') return 'in_progress';
      return 'open';
    },
    typeKey() {
      return this.constraint.type === 'bereichsuebergreifend' ? 'cross' : 'intern';
    },
    statusLabel() {
      return this.$t(`UserProjects.constraints.status.${this.statusKey}`);
    },
    typeBadgeLabel() {
      return this.$t(`UserProjects.constraints.typeBadge.${this.typeKey}`);
    },
    reportedLine() {
      return this.$t('UserProjects.constraints.reportedLine', {
        date: formatConstraintDate(this.constraint.date_created),
      });
    },
    resolvedLine() {
      return this.$t('UserProjects.constraints.resolvedLine', {
        date: formatConstraintDate(this.constraint.resolvedAt),
      });
    },
    ownerInitials() {
      return userInitials(this.constraint.ownerDisplayName);
    },
  },
};
</script>

<style scoped>
.constraint-card {
  display: block;
  width: 100%;
  text-align: left;
  padding: 1rem;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.constraint-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.constraint-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.constraint-card__title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.constraint-card__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  flex-shrink: 0;
}

.constraint-card__status-dot--in_progress {
  background: #eab308;
}

.constraint-card__status-dot--open {
  background: #ef4444;
}

.constraint-card__status-dot--resolved {
  background: #22c55e;
}

.constraint-card__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-dark);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.constraint-card__badges {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
}

.constraint-card__type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  border: 1px solid;
}

.constraint-card__type-badge--intern {
  border-color: #3b82f6;
  color: #3b82f6;
}

.constraint-card__type-badge--cross {
  border-color: var(--color-orange);
  color: var(--color-orange);
}

.constraint-card__status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.625rem;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
}

.constraint-card__status-badge--in_progress {
  background: #fefce8;
  color: #eab308;
}

.constraint-card__status-badge--open {
  background: #fef2f2;
  color: #ef4444;
}

.constraint-card__status-badge--resolved {
  background: #f0fdf4;
  color: #22c55e;
}

.constraint-card__meta {
  margin-top: 0.375rem;
  font-size: 13px;
  color: var(--color-disabled);
}

.constraint-card__footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.constraint-card__owner {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.constraint-card__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: var(--color-orange);
  color: #fff;
  font-size: 10px;
  font-weight: 500;
}

.constraint-card__owner-name {
  font-size: 12px;
  color: var(--color-disabled);
}

.constraint-card__linked {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 12px;
  color: #ababab;
}
</style>
